import BoltIcon from '@mui/icons-material/Bolt';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FlagIcon from '@mui/icons-material/Flag';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { alpha, Badge, Box, Chip, Divider, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@pulse/ui';
import { memo } from 'react';

import { projectColor } from '../utils/projectColor';

import { dueDateInfo, StatusPill } from './WorkItemRow';
import type { WorkItemRowProps } from './WorkItemRow';

// Mobile (<lg) counterpart to WorkItemRow — modeled on DepartmentCard's shape
// (organization/pages/DepartmentsPage.tsx). WorkItemRow's fixed-width columns (Priority/Due/
// Status/Hours) don't have room to sit side-by-side on a phone, so this stacks them into a
// wrapping row below the title instead of truncating or forcing horizontal scroll. Issue-only
// affordances hang off `row.issue`, exactly as in the desktop row; here the layout already
// wraps, so a Risk needs no placeholder slots. Memoized for the same reason as WorkItemRow.
export const WorkItemCard = memo(function WorkItemCard({ row, statusOptions, onToggleExpedited, canExpedite, onStatusChange, onLogTime, onOpenComments, formDialogAction, onOpenFormDialog }: WorkItemRowProps) {
  const theme = useTheme();
  const due = dueDateInfo(row.dueDate);
  const FormDialogIcon = formDialogAction?.icon;
  const expeditedColor = theme.palette.warning.main;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        ...(row.issue?.expedited && { bgcolor: alpha(expeditedColor, 0.06), borderColor: alpha(expeditedColor, 0.4) }),
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          {row.issue &&
            (canExpedite(row) ? (
              <Tooltip title={row.issue.expedited ? 'Remove from Expedite' : 'Mark as Expedite'}>
                <IconButton size="small" onClick={() => onToggleExpedited(row)} aria-label={row.issue.expedited ? `Remove ${row.title} from Expedite` : `Mark ${row.title} as Expedite`} sx={{ flexShrink: 0 }}>
                  {row.issue.expedited ? <BoltIcon color="warning" /> : <BoltOutlinedIcon sx={{ color: 'text.disabled' }} />}
                </IconButton>
              </Tooltip>
            ) : (
              // Read-only indicator — see WorkItemRow for why the un-expedited state renders
              // nothing at all here rather than a dimmed bolt.
              row.issue.expedited && (
                <Tooltip title="Expedite">
                  <BoltIcon color="warning" sx={{ flexShrink: 0, mt: 0.25 }} />
                </Tooltip>
              )
            ))}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="flex-start" spacing={0.5}>
              {row.issue?.flagged && (
                <Tooltip title="Flagged / blocked">
                  <FlagIcon sx={{ fontSize: 14, color: 'error.main', mt: 0.5, flexShrink: 0 }} />
                </Tooltip>
              )}
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{row.title}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: projectColor(row.projectId), flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" noWrap>{row.projectKey} · {row.projectName}</Typography>
            </Stack>
          </Box>
        </Stack>

        <Divider />

        <Stack direction="row" flexWrap="wrap" useFlexGap alignItems="center" gap={1}>
          {row.badge && (
            <Chip size="small" label={row.badge.label} color={row.badge.color} variant="outlined" />
          )}
          <StatusPill status={row.status} options={statusOptions} onChange={(toStep) => onStatusChange(row, toStep)} />
          <Typography variant="body2" fontWeight={500} color={due.color ?? 'text.secondary'}>{due.label}</Typography>

          {row.issue && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
              <Tooltip title={`${row.issue.totalLoggedHours}h logged`}>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <ScheduleIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                  <Typography variant="body2" fontWeight={700}>{row.issue.totalLoggedHours}h</Typography>
                </Stack>
              </Tooltip>
              <Tooltip title="Log time">
                <IconButton size="small" onClick={() => onLogTime(row)} aria-label={`Log time on ${row.title}`}>
                  <ScheduleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Comments">
                <IconButton size="small" onClick={() => onOpenComments(row)} aria-label={`Open comments on ${row.title}`}>
                  <Badge badgeContent={row.issue.commentCount} color="secondary" max={9}>
                    <ChatBubbleOutlineIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {/* Sibling of the issue cluster rather than nested inside it — the two never appear
              together (no kind has both an `issue` block and a form dialog), so each can claim
              the right edge with `ml: 'auto'` without the other having to account for it. */}
          {formDialogAction && FormDialogIcon && (
            <Tooltip title={formDialogAction.label}>
              <IconButton size="small" onClick={() => onOpenFormDialog(row)} aria-label={`${formDialogAction.label} ${row.title}`} sx={{ ml: 'auto' }}>
                <FormDialogIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
});
